import {
  adminStreamerSchema,
  streamerAccountOptionSchema,
  type AdminStreamer,
  type StreamerAccountOption,
} from '@growup/streaming-shared';
import { Autocomplete, Button, DateInput, Field, Input, Notice, todayInLisbon } from '@growup/ui';
import { useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { STREAMER_LOOK } from './labels';

const searchAccounts = (q: string) =>
  getJson(
    `/api/streaming/account-search?q=${encodeURIComponent(q)}`,
    z.array(streamerAccountOptionSchema),
  );

/**
 * Adicionar um streamer que ja era da equipa antes do site, sem candidatura
 * (GS15, decidido pelo GrowuP a 1-11-2026). A pessoa precisa de conta no site;
 * recebe um email com o link do painel. "Na equipa desde" conta para a camisola
 * de um ano.
 */
export function AddExistingStreamer({
  onAdded,
  onClose,
}: {
  onAdded: (s: AdminStreamer) => void;
  onClose: () => void;
}) {
  const [login, setLogin] = useState('');
  const [account, setAccount] = useState<StreamerAccountOption | null>(null);
  const [channel, setChannel] = useState('');
  const [joinedOn, setJoinedOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const taken =
    account !== null && account.streamerStatus !== null && account.streamerStatus !== 'removed';

  const submit = async () => {
    if (account === null) return;
    setBusy(true);
    setError(null);
    try {
      const added = adminStreamerSchema.parse(
        await send('POST', '/api/streaming/streamers', {
          accountId: account.id,
          channel,
          joinedOn: joinedOn === '' ? null : joinedOn,
        }),
      );
      onAdded(added);
      setDone(`${added.displayName} já está na equipa e recebeu um email com o link do painel.`);
      setLogin('');
      setAccount(null);
      setChannel('');
      setJoinedOn('');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Adicionar streamer da equipa"
      className="flex flex-col gap-3 border border-gu-line p-4"
    >
      <p className="text-caption text-gu-ink-muted">
        Para quem já era streamer da Grow uP antes do site. A pessoa precisa de ter conta no site;
        entra sem candidatura e recebe um email com o link do painel.
      </p>
      <Field label="Conta no site" hint="Escreva parte do login e escolha a pessoa.">
        <Autocomplete
          className="max-w-md"
          value={login}
          onChange={(v) => {
            setLogin(v);
            setAccount(null);
            setDone(null);
            setError(null);
          }}
          search={searchAccounts}
          getKey={(a) => a.id}
          renderItem={(a) => (
            <span>
              {a.login}
              {a.streamerStatus !== null ? (
                <span className="text-caption text-gu-ink-muted">
                  {' '}
                  · {STREAMER_LOOK[a.streamerStatus].label}
                </span>
              ) : null}
            </span>
          )}
          onSelect={(a) => {
            setAccount(a);
            setLogin(a.login);
          }}
        />
      </Field>
      {taken ? <Notice tone="warning">Esta conta já é streamer da equipa.</Notice> : null}
      {account !== null && !taken ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Canal da Twitch" hint="O nome do canal, como em twitch.tv/nome.">
            <Input value={channel} onChange={(e) => setChannel(e.target.value)} />
          </Field>
          <Field
            label="Na equipa desde"
            optional
            hint="Vazio = hoje. Conta para a camisola ao fim de um ano."
          >
            <DateInput value={joinedOn} onChange={setJoinedOn} max={todayInLisbon()} />
          </Field>
        </div>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {done ? <Notice tone="success">{done}</Notice> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={busy || account === null || taken || channel.trim().length < 4}
          onClick={() => void submit()}
        >
          Adicionar à equipa
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </section>
  );
}
